import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Drawer,
    IconButton,
    Grid,
    Button,
    Divider,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { isEqual, cloneDeep } from 'lodash';
import { toastr } from 'react-redux-toastr';
import InformationSection from './InformationSection';
import PeopleInformationSection from './PeopleInformationSection';
import './DataSetEdit.scss';

/**
 * @param {{
 *  open: boolean;
 *  setOpen: Function;
 *  mode: 'create' | 'edit' | 'duplicate';
 *  dataSet: any;
 *  onSave: Function;
 * }} props
 */
function DataSetEdit({ open, setOpen, mode, dataSet, onSave }) {
    const { t } = useTranslation('dataSetManager');
    const informationSectionRef = useRef(null);
    const peopleInformationSectionRef = useRef(null);

    const [formData, setFormData] = useState({
        id: null,
        name: '',
        description: '',
        fieldsSelected: [],
        status: 'Active',
    });

    const [originalData, setOriginalData] = useState({});
    const [validationErrors, setValidationErrors] = useState({
        name: false,
        fieldsSelected: false,
        nameErrorMessage: '',
    });

    useEffect(() => {
        if (open && dataSet) {
            if (mode === 'edit') {
                setFormData(cloneDeep(dataSet));
                setOriginalData(cloneDeep(dataSet));
            } else if (mode === 'duplicate') {
                const duplicatedData = {
                    ...cloneDeep(dataSet),
                    id: null,
                    name: `Copy of ${dataSet.name}`,
                };
                setFormData(duplicatedData);
                setOriginalData(duplicatedData);
            }
        } else if (open && mode === 'create') {
            const emptyData = {
                id: null,
                name: '',
                description: '',
                fieldsSelected: [],
                status: 'Active',
            };
            setFormData(emptyData);
            setOriginalData(emptyData);
        }
    }, [open, dataSet, mode]);

    useEffect(() => {
        setValidationErrors({
            name: false,
            fieldsSelected: false,
            nameErrorMessage: '',
        });
    }, [JSON.stringify(formData)]);

    const validate = () => {
        let hasError = false;
        let errorRef;
        const errors = {
            name: false,
            fieldsSelected: false,
            nameErrorMessage: '',
        };

        // Validate name
        if (!formData.name.trim()) {
            hasError = true;
            errors.name = true;
            errors.nameErrorMessage = t('validation.nameRequired');
            errorRef = informationSectionRef;
        } else if (formData.name.length > 50) {
            hasError = true;
            errors.name = true;
            errors.nameErrorMessage = t('validation.nameTooLong');
            errorRef = informationSectionRef;
        }

        // Validate fields selected
        if (!formData.fieldsSelected || formData.fieldsSelected.length === 0) {
            hasError = true;
            errors.fieldsSelected = true;
            errorRef = errorRef || peopleInformationSectionRef;
        }

        setValidationErrors(errors);

        if (hasError && errorRef?.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return !hasError;
    };

    const closeSidePanel = () => {
        setOpen(false);
        setFormData({
            id: null,
            name: '',
            description: '',
            fieldsSelected: [],
            status: 'Active',
        });
        setValidationErrors({
            name: false,
            fieldsSelected: false,
            nameErrorMessage: '',
        });
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        const dataToSave = {
            name: formData.name,
            description: formData.description,
            fieldsSelected: formData.fieldsSelected,
        };

        // For edit mode, include the ID
        if (mode === 'edit') {
            dataToSave.id = formData.id;
        }

        // For duplicate mode, include the source ID
        if (mode === 'duplicate') {
            dataToSave.sourceId = dataSet.id;
        }

        try {
            await onSave(dataToSave, mode);
            // Success message is handled in the parent component
        } catch (error) {
            // exception not throw
        }
    };

    const updateFormData = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const isDirty = !isEqual(originalData, formData) || mode === 'duplicate';
    let stepIndex = 1;

    const getTitle = () => {
        if (mode === 'create') return t('sidePanel.createTitle');
        if (mode === 'edit') return t('sidePanel.editTitle');
        if (mode === 'duplicate') return t('sidePanel.duplicateTitle');
        return t('sidePanel.title');
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            classes={{ paper: 'dataSetEditDrawerPaper' }}
        >
            <Grid
                container
                justifyContent="space-between"
                className="dse-header-section"
            >
                <Grid
                    item
                    className="title-container"
                >
                    <h3 className="title-label">
                        {getTitle()}
                    </h3>
                </Grid>
                <Grid item>
                    <IconButton
                        className="drawer-x-icon-button"
                        onClick={() => closeSidePanel()}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            size="sm"
                        />
                    </IconButton>
                </Grid>
            </Grid>
            <Grid
                container
                className="dse-content-section"
            >
                <InformationSection
                    stepIndex={stepIndex++}
                    errorRef={informationSectionRef}
                    validationErrors={validationErrors}
                    formData={formData}
                    updateFormData={updateFormData}
                />
                <PeopleInformationSection
                    stepIndex={stepIndex++}
                    errorRef={peopleInformationSectionRef}
                    validationErrors={validationErrors}
                    formData={formData}
                    updateFormData={updateFormData}
                />
            </Grid>
            <Divider />
            <Grid
                container
                justifyContent="flex-end"
                className="dse-footer-section"
            >
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!isDirty}
                >
                    {mode === 'create' ? t('actions.create') : t('actions.save')}
                </Button>
                <Button
                    variant="contained"
                    onClick={closeSidePanel}
                >
                    {t('actions.cancel')}
                </Button>
            </Grid>
        </Drawer>
    );
}

export default DataSetEdit;

